<template>
  <div v-if="platformPermissionsLoaded && checkPermission('insights.crossover.participation')">
    <SmartFilters ref="smartFilters" :parent="'core_participation_insights'"/>

    <CRow>
      <CCol col="12" xl="12">
        <CCard class="mb-0">
          <CCardHeader>
            <CRow>
              <CCol cols="4" xl="4" class="pt-0 pb-0 text-left">
                {{ $t('insights.Overview_participation') }}
              </CCol>
              <CCol cols="8" xl="8" class="pt-0 pb-0 text-xl-right">
                <b-switch class="mb-0 mt-1 mt-xl-0 enable_search" v-model="searchEnabled" size="is-small">{{ $t('common.activate_search') }}</b-switch>
                <div class="mt-1 mt-xl-0 d-inline-block align-top">
                  <CButton class="ml-2 link" color="primary" @click="exportData('csv')">
                    <i class="fas fa-file-arrow-down mr-1"/><span>{{ $t('common.Export_as_csv') }}</span>
                  </CButton>                
                  <CButton class="ml-2 link" color="primary" @click="exportData('json');">
                    <i class="fas fa-file-arrow-down mr-1"/><span>{{ $t('common.Export_as_json') }}</span>              
                  </CButton>
                </div>              
              </CCol>  
            </CRow>
          </CCardHeader>
          <CCardBody class="p-0">
            <CRow>
              <CCol cols="12" lg="12" :class="{'pt-0 pb-0' : !loadingInsightsData}">
                <div v-if="loadingInsightsData" class="h-100 d-flex align-items-center">
                  <loadingSpinner mode="auto" :content="null"/>
                </div>                
                <b-table v-else 
                         class="data_table"
                         style="overflow: auto !important;" 
                         :data="audienceParticipation" 
                         :striped="true" 
                         :hoverable="true" 
                         :mobile-cards="true" 
                         :paginated="isPaginated" 
                         :per-page="perPage" 
                         :current-page.sync="currentPage" 
                         :pagination-simple="isPaginationSimple" 
                         :pagination-position="paginationPosition" 
                         :default-sort-direction="defaultSortDirection" 
                         :sort-icon="sortIcon" 
                         :sort-icon-size="sortIconSize" 
                         default-sort="Name">
                  
                  <template slot-scope="props">
                    <b-table-column field="name" :label="$t('common.Name')" :searchable="searchEnabled" :sortable="true">
                      <span class="no-wrap">{{props.row.name}}</span>
                    </b-table-column>
                    <b-table-column field="function" :label="$t('common.Function')" :searchable="searchEnabled" :sortable="true">
                      <span class="no-wrap">{{props.row.function ? props.row.function : '-'}}</span>
                    </b-table-column>
                    <b-table-column field="department" :label="$t('common.Department')" :searchable="searchEnabled" :sortable="true">
                      <span class="no-wrap">{{props.row.department}}</span>
                    </b-table-column>
                    <b-table-column field="team" :label="$t('common.Team')" :searchable="searchEnabled" :sortable="true">
                      <span class="no-wrap">{{props.row.team}}</span>
                    </b-table-column>
                    <b-table-column field="user_active" :label="$t('common.Active')" :searchable="searchEnabled" :sortable="true">
                      <span>{{props.row.user_active}}</span>
                    </b-table-column>
                    <b-table-column field="total_activity_count" :label="$t('insights.Total_activities')" :searchable="false" :sortable="true">
                      <span>{{props.row.total_activity_count}}</span>
                    </b-table-column>
                    <b-table-column field="last_new_login" :label="$t('common.Last_activity')" :searchable="false" :sortable="true">
                      <span v-if="props.row.last_app_activity !== null" class="no-wrap">{{props.row.last_app_activity | moment("DD-MM-YYYY HH:ss")}}</span>
                      <span v-else>-</span>
                    </b-table-column>
                    <b-table-column field="last_sw_post_viewed" :visible="modules.social_wall === 1" :label="$t('insights.Last_seen_sw_post2')" :searchable="searchEnabled" :sortable="true">
                      <span v-line-clamp="1">{{props.row.last_sw_post_viewed}}</span>
                    </b-table-column>
                    <b-table-column field="last_sw_post_viewed_at" :visible="modules.social_wall === 1" :label="$t('insights.Post_seen_at2')" :searchable="false" :sortable="true">
                      <span v-if="props.row.last_sw_post_viewed_at !== null" class="no-wrap">{{props.row.last_sw_post_viewed_at | moment("DD-MM-YYYY HH:ss")}}</span>
                      <span v-else>-</span>                      
                    </b-table-column>
                    <b-table-column field="last_lms_step" :visible="modules.academy === 1" :label="$t('insights.Last_lms_step2')" :searchable="searchEnabled" :sortable="true">
                      <span class="no-wrap">{{props.row.last_lms_step ? props.row.last_lms_step : '-'}}</span>
                    </b-table-column>
                    <b-table-column field="last_lms_step_started_at" :visible="modules.academy === 1" :label="$t('insights.LMS_step_started2')" :searchable="false" :sortable="true">                      
                      <span v-if="props.row.last_lms_step_started_at !== null" class="no-wrap">{{props.row.last_lms_step_started_at | moment("DD-MM-YYYY HH:ss")}}</span>
                      <span v-else>-</span>
                    </b-table-column>
                    <b-table-column field="last_lms_step_finished_at" :visible="modules.academy === 1" :label="$t('insights.LMS_step_finished2')" :searchable="false" :sortable="true">
                      <span v-if="props.row.last_lms_step_finished_at !== null">{{props.row.last_lms_step_finished_at | moment("DD-MM-YYYY HH:ss")}}</span>
                      <span v-else>-</span>
                    </b-table-column>
                    <b-table-column field="current_loyalty_points" :visible="modules.employee_loyalty_programme === 1" :label="$t('insights.Loyalty_points')" :searchable="searchEnabled" :sortable="true">
                      <div class="d-flex align-items-center credits pointer no-wrap">
                        <img :src="apiBaseUrl + '/v1/common/cdn/file/image/base-dashboard/credits/' + environmentTag + '_credit.png/' + clientToken"/>
                        <span>{{props.row.current_loyalty_points}} {{props.row.current_loyalty_points !== 1 ? $t('common.coins') : $t('common.coin')}}</span>
                      </div>
                    </b-table-column>
                  </template>
                  <template slot="empty">
                    <div class="p-2 text-center">                      
                      <span>{{$t('insights.Statistics_not_found')}}</span>
                    </div>                
                  </template>                
                </b-table>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
  <noPermission v-else-if="platformPermissionsLoaded" trigger="permission"/>
</template>

<script>
import axios from 'axios'
import loadingSpinner from '@/components/common/loadingSpinner.vue'
import noPermission from '@/components/common/noPermission.vue';

import SmartFilters from '@/components/insights/common/SmartFilters.vue';

export default {
  name: 'InsightsParticipation',
  components: {
    loadingSpinner,
    noPermission,
    SmartFilters
  },
  data: () => {
    return {
      modules: {
        social_wall: 0,
        academy: 0,
        employee_loyalty_programme: 0
      },
      apiBaseUrl: null,
      clientToken: null,
      environmentTag: null,      
      platformPermissions: [],
      platformPermissionsLoaded: false,
      audienceParticipation: [],
      overviewDataLoaded: false,
      isPaginated: true,
      isPaginationSimple: false,
      paginationPosition: 'bottom',
      currentPage: 1,
      perPage: 10,
      defaultSortDirection: 'asc',
      sortIcon: 'arrow-up',
      sortIconSize: 'is-small',
      showInactive: false,
      searchEnabled: false,
      loadingInsightsData: false,
      smartFilters: []
    }
  },
  methods: {
    getAudienceParticipation() {
      let params = {};
      params.filters = this.smartFilters;
      // Start the loader
      this.loadingInsightsData = true;
      // Get the participation insights data
      axios.post(process.env.VUE_APP_API_URL + '/v1/insights/audience/participation', params)
      .then(res => {
        this.audienceParticipation = res.data.data;
        // Check if the pagination should be activated
        (this.audienceParticipation.length > this.perPage) ? this.isPaginated = true : this.isPaginated = false;         
        // Hide the loader
        this.loadingInsightsData = false;
      })
      .catch(err => {
        console.error(err); 
      });
    },
    exportData(outputFormat){
      let params = {};
      params.filters = this.smartFilters;
      // Get the participation insights data            
      axios.post(process.env.VUE_APP_API_URL + '/v1/insights/audience/participation/export/' + outputFormat, params)
      .then(res => {
        this.$buefy.toast.open({ message: this.$t('common.Export_ready'), type: 'is-success', duration: 2000 });
        if (!window.navigator.msSaveOrOpenBlob){
          // Blob navigator
          let url = null;
          let outputData = null;
          // Set the output data and URL based on the output format
          if(outputFormat === 'csv') {
            outputData = res.data;
            url = window.URL.createObjectURL(new Blob([outputData]));  
          } else if(outputFormat === 'json') {
            outputData = JSON.stringify(res.data.data);
            url = window.URL.createObjectURL(new Blob([outputData]));
          }
          // Create a new 'a' element
          const link = document.createElement('a');
          // Set the generated URL as href of the new element
          link.href = url;
          // Set the download attribute based on the output format
          if(outputFormat === 'csv') {
            link.setAttribute('download', 'insights-audience-participation.csv');
          } else if(outputFormat === 'json') {
            link.setAttribute('download', 'insights-audience-participation.json');
          }
          // Append the link to the body
          document.body.appendChild(link);
          // Click the link
          link.click();
        } else {
          // Blob for Explorer 11
          let url = null;
          let outputData = null;
          // Set the output data and URL based on the output format
          if(outputFormat === 'csv') {
            outputData = res.data;
            url = window.navigator.msSaveOrOpenBlob(new Blob([outputData]),"insights-audience-participation.csv");
          } else if(outputFormat === 'json') {
            outputData = JSON.stringify(res.data.data);
            url = window.navigator.msSaveOrOpenBlob(new Blob([outputData]),"insights-audience-participation.json");
          }
        }
      })
      .catch(err => {
        console.error(err); 
      });      
    },
    getPlatformPermissions() {
      axios.get(process.env.VUE_APP_API_URL + '/v1/core/platform/permissions')
      .then(res => {      
        this.platformPermissions = res.data.data;
        // Update the platformPermissionsLoaded value
        this.platformPermissionsLoaded = true;
      })
      .catch(err => {
        console.error(err); 
      });
    },
    checkPermission(permissionTag) {
      if(this.platformPermissions.includes(permissionTag)) {
        return true;
      } else{
        return false;
      }
    },
    checkModules() {
      axios.get(process.env.VUE_APP_API_URL + '/v1/core/platform/modules')
      .then(res => {      
        this.modules = res.data.data;    
      })
      .catch(err => {
        console.error(err); 
      });
    }    
  },
  mounted: function() {
    this.apiBaseUrl = process.env.VUE_APP_API_URL;
    this.clientToken = localStorage.getItem('token');
    if(localStorage.getItem('environmentTag') !== null) this.environmentTag = localStorage.getItem('environmentTag');

    this.checkModules();
    this.getPlatformPermissions();
    this.getAudienceParticipation();
    
    this.$bus.$on('core_participation_insights_smart_filters_updated', (filters) => {
      this.smartFilters = filters;
      this.getAudienceParticipation();
    });    
  },
  beforeDestroy() {
    this.$bus.$off('core_participation_insights_smart_filters_updated');
  }  
}
</script>
